import { useState, useEffect, useCallback } from 'react';
import { queryEinstein, getEinsteinSuggestions, getEinsteinConversation, deleteEinsteinConversation } from '../api';

// Shared Einstein chat state machine — extracted 2026-09-03 so the compact Home console
// (EinsteinConsole.js) and the dedicated full-page Einstein tab (pages/EinsteinChat.js) run the
// exact same send/priorContext/role logic instead of two copies drifting apart. Only the layout
// differs between them; this file owns none of it.
//
// Conversation persistence (2026-09-10): every sent turn is saved server-side
// (einstein_conversations/einstein_messages, POST /api/einstein/query) under a conversationId
// this hook tracks — lazily created on the first message of a fresh thread, reused after that.
// loadConversation() re-hydrates a past thread from history (the dedicated tab's sidebar);
// startNewConversation() resets back to a blank greeting. priorContext (a needs_clarification
// follow-up) is still live-session-only — it doesn't survive a reload of the same conversation,
// same as before.

export function firstName(name) {
  return (name || '').trim().split(/\s+/)[0] || 'there';
}

export function prettyKey(key) {
  return (key || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

// A router response's `route` (e.g. "/app?page=gps&gpsTab=match-chronic&player_id=82") is a
// full URL, but this SPA's page switch (App.js's handlePageChange) and its filter params
// (per-page useUrlParam hooks) are two separate mechanisms — `page`/`gpsTab` drive which
// component App.js renders, everything else is just URL state a leaf page reads on its own.
// Splitting them here is what callers (Home.js/EinsteinChat.js's goToRoute) need to drive both
// correctly instead of just changing the URL and hoping the page follows (it doesn't — see
// handlePageChange's extraParams comment in App.js for why that silently did nothing before).
export function parseEinsteinRoute(route) {
  const qIndex = route.indexOf('?');
  const params = new URLSearchParams(qIndex >= 0 ? route.slice(qIndex + 1) : '');
  const page = params.get('page');
  const gpsTab = params.get('gpsTab');
  params.delete('page');
  params.delete('gpsTab');
  return { page, gpsTab, params };
}

let msgSeq = 0;
const nextId = () => `m${++msgSeq}`;

export function useEinsteinChat({ user }) {
  const [thread, setThread] = useState([]);
  const [draft, setDraft] = useState('');
  const [status, setStatus] = useState('idle'); // idle | thinking | loading | error
  const [suggestions, setSuggestions] = useState([]);
  // { previousQuestion, clarifyingQuestion } while the last turn was a needs_clarification,
  // else null — doesn't survive loadConversation()/startNewConversation() (see header comment).
  const [priorContext, setPriorContext] = useState(null);
  // null until the first message of a thread is sent or a past thread is loaded — see
  // header comment. Passed on every send() so the backend appends to the same conversation
  // instead of starting a new one each turn.
  const [conversationId, setConversationId] = useState(null);

  // Logins from before session tokens existed (added 2026-08-24) are still sitting in
  // localStorage — this app keeps `authUser` indefinitely with no forced re-login (see
  // DEPLOYMENT.md) — so `user.sessionToken` can be silently missing for an otherwise-"logged in"
  // account. Surfaced explicitly (2026-09-03 bug report: this looked exactly like "I can't send
  // a message" when it silently no-op'd instead).
  const hasSessionToken = !!user?.sessionToken;

  const loadSuggestions = useCallback(() => {
    if (!user?.sessionToken) return;
    getEinsteinSuggestions(user.sessionToken)
      .then(r => setSuggestions(r?.suggestions || []))
      .catch(() => {});
  }, [user?.sessionToken]);

  // Opening message (Home redesign Task 4): a deterministic, factual-only greeting — no
  // squad-level 28-day baseline is stored anywhere yet (checked pillarScores.js directly), so
  // per the handoff's own documented fallback this is the neutral greeting, not a templated
  // insight. Not an LLM call, so nothing to wait on.
  useEffect(() => {
    if (!hasSessionToken) {
      setThread([{ id: nextId(), role: 'einstein', kind: 'error', text: 'Your login is from before Einstein was added and is missing a piece it needs. Sign out and back in, then come back — your other pages are unaffected.' }]);
      return;
    }
    setThread([{ id: nextId(), role: 'einstein', kind: 'text', text: `Goodday ${firstName(user?.name)}, with what can I help you today sir?` }]);
    setConversationId(null);
    setPriorContext(null);
    loadSuggestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, hasSessionToken]);

  const send = useCallback((questionText) => {
    const question = (questionText ?? draft).trim();
    if (!question || status === 'thinking') return;
    if (!user?.sessionToken) {
      setThread(t => [...t, { id: nextId(), role: 'einstein', kind: 'error', text: 'Sign out and back in to enable Einstein chat.' }]);
      return;
    }
    setThread(t => [...t, { id: nextId(), role: 'coach', text: question }]);
    setDraft('');
    setStatus('thinking');
    setSuggestions([]); // clear immediately on send, not just after the answer lands (2026-09-03 fix)
    const askedPriorContext = priorContext;
    setPriorContext(null);

    queryEinstein(user.sessionToken, question, askedPriorContext, conversationId)
      .then(res => {
        setStatus('idle');
        if (res?.conversationId) setConversationId(res.conversationId);
        if (!res || res.status === 'error') {
          setThread(t => [...t, { id: nextId(), role: 'einstein', kind: 'error', text: res?.reason || 'Something went wrong on my end — try again.' }]);
          return;
        }
        if (res.status === 'needs_clarification') {
          setPriorContext({ previousQuestion: question, clarifyingQuestion: res.question });
          setThread(t => [...t, { id: nextId(), role: 'einstein', kind: 'needs_clarification', data: res }]);
          return;
        }
        setThread(t => [...t, { id: nextId(), role: 'einstein', kind: res.tier, data: res }]);
        // Suggestion chips are hidden for good once the trainer sends a first message (2026-09-10
        // — more room for the conversation itself), so there's no longer any point re-fetching a
        // set that will never render again.
      })
      .catch(err => {
        setStatus('error');
        setThread(t => [...t, { id: nextId(), role: 'einstein', kind: 'error', text: err.message || 'Could not reach Einstein.' }]);
      });
  }, [draft, status, user, priorContext, conversationId]);

  const onOptionClick = useCallback((opt, clarifyingQuestion) => {
    setPriorContext(prev => prev || { previousQuestion: thread[thread.length - 2]?.text || '', clarifyingQuestion });
    send(opt);
  }, [thread, send]);

  // Back to a blank thread under a fresh (not-yet-created) conversation — the dedicated tab's
  // "New chat" action. Doesn't touch the backend; a conversation only exists once send() actually
  // posts a first message to it, so there's nothing to delete here.
  const startNewConversation = useCallback(() => {
    setThread([{ id: nextId(), role: 'einstein', kind: 'text', text: `Goodday ${firstName(user?.name)}, with what can I help you today sir?` }]);
    setConversationId(null);
    setPriorContext(null);
    setDraft('');
    setStatus('idle');
    loadSuggestions();
  }, [user, loadSuggestions]);

  // Re-hydrates a past conversation from history (sidebar row click) into the exact same thread
  // shape a live turn produces (role/kind/text/data — see server.staging.js's
  // GET /api/einstein/conversations/:id), so EinsteinMessage.js renders it identically either way.
  const loadConversation = useCallback((id) => {
    if (!user?.sessionToken) return;
    setStatus('loading');
    getEinsteinConversation(user.sessionToken, id)
      .then(res => {
        setThread((res?.messages || []).map(m => ({ id: nextId(), ...m })));
        setConversationId(id);
        setPriorContext(null);
        setDraft('');
        setStatus('idle');
      })
      .catch(err => {
        setStatus('error');
        setThread(t => [...t, { id: nextId(), role: 'einstein', kind: 'error', text: err.message || 'Could not load that conversation.' }]);
      });
  }, [user]);

  // Deletes a conversation from history (sidebar row's delete action). If it's the one currently
  // open, falls back to a fresh blank thread the same way startNewConversation does — the deleted
  // conversation obviously can't stay on screen. Returns the request promise so the caller (the
  // sidebar) can re-fetch its list once the delete actually lands, rather than assuming success.
  const deleteConversation = useCallback((id) => {
    if (!user?.sessionToken) return Promise.resolve();
    return deleteEinsteinConversation(user.sessionToken, id).then(() => {
      if (id === conversationId) startNewConversation();
    });
  }, [user, conversationId, startNewConversation]);

  // True from the trainer's first sent message onward (a needs_clarification option click counts
  // too — send() posts it as a normal 'coach' message either way) — both consumers hide the
  // suggestion row once this flips, trading it for more room to read the conversation
  // (2026-09-10). Derived from `thread` rather than a separate flag so it can't drift out of sync
  // with what's actually on screen.
  const hasChatted = thread.some(m => m.role === 'coach');

  return {
    thread, draft, setDraft, status, suggestions, hasSessionToken, hasChatted, send, onOptionClick,
    conversationId, startNewConversation, loadConversation, deleteConversation,
  };
}
